import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Users, Link2, Shield, Loader2 } from 'lucide-react'
import { PermissionsList } from './PermissionsList'
import { ShareLinksList } from './ShareLinksList'
import { templatePermissionService } from '@/lib/api/services/templatePermissions'
import { templateShareLinkService } from '@/lib/api/services/templateShareLinks'
import type {
  TemplatePermissionsDetail,
  TemplateShareLink,
  PermissionLevel,
} from '@/lib/api/types'
import { useToast } from '@/components/ui/use-toast'

interface TemplateSharingPanelProps {
  templateId: string
  templateName: string
  effectivePermission: PermissionLevel
}

export function TemplateSharingPanel({
  templateId,
  templateName,
  effectivePermission,
}: TemplateSharingPanelProps) {
  const [activeTab, setActiveTab] = useState('people')
  const [loading, setLoading] = useState(true)
  const [permissionsDetail, setPermissionsDetail] =
    useState<TemplatePermissionsDetail | null>(null)
  const [shareLinks, setShareLinks] = useState<TemplateShareLink[]>([])
  const { toast } = useToast()

  const canManagePermissions =
    effectivePermission === 'Admin' || effectivePermission === 'Edit'

  useEffect(() => {
    loadData()
  }, [templateId])

  const loadData = async () => {
    setLoading(true)
    try {
      const [permissions, links] = await Promise.all([
        templatePermissionService.getPermissions(templateId),
        canManagePermissions
          ? templateShareLinkService.getShareLinks(templateId)
          : Promise.resolve([]),
      ])
      setPermissionsDetail(permissions)
      setShareLinks(links)
    } catch (error) {
      toast({
        title: 'Error',
        description:
          error instanceof Error ? error.message : 'Failed to load sharing data',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const handlePermissionsChange = () => {
    loadData()
  }

  const handleShareLinksChange = async () => {
    try {
      const links = await templateShareLinkService.getShareLinks(templateId)
      setShareLinks(links)
    } catch (error) {
      toast({
        title: 'Error',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to refresh share links',
        variant: 'destructive',
      })
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div>
              <CardTitle>Sharing & Permissions</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Manage who can access and edit this template
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="people" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                People with Access
              </TabsTrigger>
              {canManagePermissions && (
                <TabsTrigger value="links" className="flex items-center gap-2">
                  <Link2 className="h-4 w-4" />
                  Share Links
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="people" className="mt-0">
              <PermissionsList
                templateId={templateId}
                permissions={permissionsDetail?.permissions || []}
                canManage={canManagePermissions}
                onPermissionsChange={handlePermissionsChange}
              />
            </TabsContent>

            {canManagePermissions && (
              <TabsContent value="links" className="mt-0">
                <ShareLinksList
                  templateId={templateId}
                  templateName={templateName}
                  shareLinks={shareLinks}
                  onShareLinksChange={handleShareLinksChange}
                />
              </TabsContent>
            )}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  )
}
